/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import React, { useMemo, useCallback } from 'react';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { ABodySize, MButton, MButtonSize, MButtonType } from '@marriott/mi-web-ui-kit';
import { buildChatAvailabilityUrl, usePageContext } from '@marriott/mi-shop-components';
import { SaveHotelButton } from '../MSaveHotelButton';
import { eventUtil } from '@marriott/mi-ui-library';
import { MChatImageCarousel } from '../MChatImageCarousel';
import { MChipGroup } from '../MChipGroup';
import { MPropertyPriceSection } from '../MPropertyPriceSection';
import { MPropertyReviewRating } from '../MPropertyReviewRating/index';
import { MPropertyLocationSummary } from '../MPropertyLocationSummary';
import { MResponseActions } from '../MResponseActions';
import { useMarriottAIPreviewStore } from '../../store';
import {
  getPropertyCardsToRender,
  getRawSearchTotal,
  toPropertyCardViewModel,
  getRawSearchEdgesTotal,
} from '../../utils';
import { PropertyCardSourceData } from '../../types';
import { AI_SEARCH_CLICK_TRACK } from '../../utils/acdlUtils';
import { useHqvMarkdownComponents } from '../../hooks/useHqvMarkdownComponents';
import { mapRateRequestToSpecialRates, buildChatSearchCriteriaFromAmpRequest } from '../../utils/ampRequestMapper';
import { MRW_CODE, P17_CODE } from '../MPropertyPriceSection/MPropertyPriceSection.constants';
import { HWSBrands } from '../../organisms/OSearchResults/index.constant';
import { trcRedirectionVarValue } from '../../constants';
import { computeRedirectionUrl } from '../../utils/computeRedirectionUrl';
import * as Styled from './MIntegratedImagery.styles';
import {
  MIntegratedImageryProps,
  PropertyCardTemplateEntry,
  SearchEdge,
  UiPropertyItem,
} from './MIntegratedImagery.types';

/**
 * Check if the ampRequest contains points-related rate codes (MRW or P17)
 * This determines if the original search was a points search
 */
function isPointsSearchFromAmpRequest(ampRequest: any): boolean {
  if (!ampRequest) return false;

  const rateRequests = ampRequest?.productQueriesHospitality?.priceModifiers?.rateRequest;
  if (!rateRequests || !Array.isArray(rateRequests)) return false;

  // Check if any rate request has MRW or P17 code (points-related codes)
  return rateRequests.some(
    (req: { rateRequestValue?: string }) => req?.rateRequestValue === MRW_CODE || req?.rateRequestValue === P17_CODE
  );
}

export const MIntegratedImagery: React.FC<MIntegratedImageryProps> = ({
  uiData,
  viewData,
  onOpenHotelPanel,
  onChipClick,
  onViewAllProperties,
  messageId,
  thumbFeedback,
  onThumbFeedback,
}) => {
  // Preview panel may already have loaded media for properties; reuse it in carousel to avoid refetching.
  const propertyMediaById = useMarriottAIPreviewStore(state => state.propertyMediaById);
  // Get all messages from current chat session (only used as fallback when viewData is not provided).
  const messages = useMarriottAIPreviewStore(state => state.messages);

  // Page-level config for URL base
  const { NEXT_PUBLIC_AVAILABILITY_SEARCH_URL } = usePageContext();

  // Extract ampRequest and isFlexibleDateSearch from viewData for edge case handling
  const ampRequest = viewData?.ampRequest;
  const isFlexibleDateSearch = viewData?.isFlexDate ?? false;

  // Derive isPointsSearch from ampRequest instead of store state
  // This ensures the display mode matches the original search criteria for this message
  const isPointsSearch = useMemo(() => isPointsSearchFromAmpRequest(ampRequest), [ampRequest]);

  // Build chatSearchCriteria from ampRequest for URL construction
  const chatSearchCriteria = useMemo(() => {
    const llmMetadata = (viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery as any)
      ?.llmMetadata;
    return buildChatSearchCriteriaFromAmpRequest(ampRequest, llmMetadata, isFlexibleDateSearch);
  }, [ampRequest, viewData, isFlexibleDateSearch]);

  // Helper function to build availability URL for a property
  const buildPropertyAvailabilityUrl = (propertyId: string): string => {
    if (!NEXT_PUBLIC_AVAILABILITY_SEARCH_URL || !propertyId) return '';

    return buildChatAvailabilityUrl({
      baseUrl: NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      propertyCode: propertyId,
      chatSearchCriteria: chatSearchCriteria ?? undefined,
      forceRateCalendar: isFlexibleDateSearch,
    });
  };

  // Compute isSpecialRateSearch from ampRequest (same logic as MPropertyCardV0)
  const isSpecialRateSearch = useMemo(() => {
    if (!ampRequest) return false;

    const rateRequests = ampRequest?.productQueriesHospitality?.priceModifiers?.rateRequest;
    const { specialRates } = mapRateRequestToSpecialRates(rateRequests);

    const clusterCode = specialRates?.clusterCode ?? '';
    const corporateCode = specialRates?.corporateCode ?? '';
    const groupCode = specialRates?.groupCode ?? '';

    return Boolean(clusterCode || corporateCode || groupCode);
  }, [ampRequest]);

  // Extract childCount from ampRequest guestCounts (guests with age are children)
  const childCount = useMemo(() => {
    const guestCounts =
      ampRequest?.productQueriesHospitality?.roomStayQueryProduct?.baseHospitalityFacilityQuery?.guestCounts ?? [];
    return guestCounts.filter((g: any) => g.age !== null && g.age !== undefined).length;
  }, [ampRequest]);

  // Get HQV markdown components to intercept hqv:// links in intro/followUp text
  const hqvComponents = useHqvMarkdownComponents(onOpenHotelPanel, viewData);

  // Rendering priority:
  // Render only formatter-provided ordered property IDs.
  // Use edges from THIS message's viewData to ensure prices match the search criteria
  // that was active when this message was created. This prevents price inconsistency
  // when users modify search criteria or view older messages.
  const propertyCardSelection = useMemo(() => {
    return getPropertyCardsToRender<UiPropertyItem, SearchEdge>(uiData?.properties, messages, viewData);
  }, [uiData?.properties, messages, viewData]);
  const edges = propertyCardSelection.edges;
  const propertyCards = propertyCardSelection.propertyCards;

  // Extract geoCodeInfo from viewData for location summary fallback
  const geoCodeInfo = viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.geoCodeInfo;

  const propertyViewModels = useMemo(
    () =>
      propertyCards.map((entry: PropertyCardTemplateEntry, idx: number) =>
        toPropertyCardViewModel(entry, idx, geoCodeInfo)
      ),
    [propertyCards, geoCodeInfo]
  );

  // Narrative sections are optional and may not exist in every response payload.
  const intro = typeof uiData?.intro === 'string' ? uiData.intro : '';
  const followUp = typeof uiData?.followUp === 'string' ? uiData.followUp : '';
  // Chips are follow-up prompts from formatter output; keep shape flexible and guard with array check.
  const chips = Array.isArray(uiData?.mChipGroup?.mQuickSelectChip) ? uiData.mChipGroup!.mQuickSelectChip : [];
  const chipItems = useMemo(
    () =>
      chips.map((chip: { text?: string }) => ({
        aBody: {
          bodyText: chip?.text || '',
          size: ABodySize.S,
        },
        onClick: () => {
          if (chip?.text) {
            onChipClick?.(chip.text);
          }
        },
      })),
    [chips, onChipClick]
  );
  // "View all" count prefers backend totals; falls back to current edges length when totals are absent.
  const totalResults =
    getRawSearchEdgesTotal(viewData as PropertyCardSourceData<SearchEdge>) ??
    getRawSearchTotal(viewData as PropertyCardSourceData<SearchEdge>) ??
    edges.length;
  const handlePropertyTitleHover = (propertyId?: string): void => {
    if (!propertyId) return;
    eventUtil?.dispatch('propCardOver', { id: propertyId });
  };
  const handlePropertyTitleUnhover = (): void => {
    eventUtil?.dispatch('propCardUnOver', null);
  };

  // Helper to get the current locale for HWS URL construction
  const getCurrentLocale = useCallback((): string => {
    if (typeof document !== 'undefined' && document.documentElement?.lang) {
      return document.documentElement.lang;
    }
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }
    return 'en-US';
  }, []);

  // Helper to check if a brand is an HWS brand and get the redirect URL
  const getHwsRedirectUrl = useCallback(
    (brandCode?: string, propertyId?: string, propertyName?: string, seoNickname?: string | null): string | null => {
      if (!brandCode || !HWSBrands.includes(brandCode)) {
        return null;
      }
      const locale = getCurrentLocale();
      return computeRedirectionUrl(brandCode, propertyId, propertyName, trcRedirectionVarValue, locale, seoNickname);
    },
    [getCurrentLocale]
  );

  // Handler for property click - opens HWS for Ritz-Carlton brands, HQV panel for others
  const handlePropertyClick = useCallback(
    (vm: any, options?: { openGallery?: boolean }) => {
      // Try to get brand code from multiple possible locations in the data structure
      const brandCode =
        vm.propertyData?.basicInformation?.brand?.id || vm.edge?.node?.property?.basicInformation?.brand?.id;

      const hwsUrl = getHwsRedirectUrl(
        brandCode,
        vm.propertyId,
        vm.propertyName,
        vm.propertyData?.seoNickname || vm.edge?.node?.property?.seoNickname
      );

      if (hwsUrl) {
        window.open(hwsUrl, '_blank', 'noopener,noreferrer');
        return; // Don't open HQV panel for HWS brands
      }

      onOpenHotelPanel?.(vm.propertyData, vm.propertyId, { ...options, messageViewData: viewData });
    },
    [getHwsRedirectUrl, onOpenHotelPanel, viewData]
  );

  return (
    <Styled.Container className="d-flex flex-column">
      {/* Intro copy from formatter response - use hqvComponents to intercept hqv:// links */}
      {intro ? (
        <div className="t-font-s">
          <MarkdownPreview source={intro} components={hqvComponents} />
        </div>
      ) : null}

      {propertyViewModels.map(vm => {
        const hasReviewSummary = vm.ratingInfo.hasRating && vm.ratingInfo.reviewCountValue > 0 && !vm.ratingInfo.isMax;
        const hasLocationSummary = Boolean(vm.locationSummary?.city || vm.locationSummary?.state);

        return (
          <Styled.PropertyBlock key={vm.key} className="d-flex flex-column">
            <div className="d-flex flex-column">
              {/* Property identity + primary actions */}
              <div className="d-flex align-items-center justify-content-between pr-4">
                <Styled.PropertyNameButton
                  type="button"
                  aria-label={`Open details for ${vm.propertyName}`}
                  onClick={() => handlePropertyClick(vm)}
                  onMouseEnter={() => handlePropertyTitleHover(vm.propertyId)}
                  onMouseLeave={handlePropertyTitleUnhover}
                  className="text-left px-0 t-subtitle-m"
                >
                  <MarkdownPreview source={vm.propertyName} />
                </Styled.PropertyNameButton>
                {/* WIP - dispatch function in button is read from context in search page. It is giving error */}
                <Styled.FavoriteSaveButtonWrapper>
                  <SaveHotelButton
                    propertyID={vm.propertyId}
                    clickTrackValue={AI_SEARCH_CLICK_TRACK.FAVORITE_HOTEL}
                    showVisibleLabel
                  />
                </Styled.FavoriteSaveButtonWrapper>
              </div>

              {/* Ratings and pricing summary row */}
              <Styled.RatingsRow>
                {hasReviewSummary ? <MPropertyReviewRating ratingInfo={vm.ratingInfo} /> : null}
                {hasLocationSummary ? <MPropertyLocationSummary locationSummary={vm.locationSummary} /> : null}
                <Styled.PriceSectionWrapper className="mt-2">
                  <MPropertyPriceSection
                    edge={vm.edge ?? undefined}
                    priceDisplay={vm.priceDisplay}
                    showDivider={hasReviewSummary}
                    displayMode={isPointsSearch ? 'points' : 'cash'}
                    soldOutLabel="Sold Out"
                    rateUnavailableLabel="Rate Unavailable"
                    isSpecialRateSearch={isSpecialRateSearch}
                    isFlexibleDateSearch={isFlexibleDateSearch}
                    openingDate={vm.propertyData?.basicInformation?.openingDate}
                    ampRequest={ampRequest}
                    callToBookLabel="Call to Book"
                    openingSoonLabel="Opening"
                    openingSoonDateFormat="MMMM YYYY"
                    isBookable={vm.propertyData.basicInformation?.bookable}
                    maxOccupancyLabel="To see rate, edit guests or rooms."
                    adultsOnlyLabel="Please remove child guest(s)."
                    isAdultsOnly={vm.propertyData.basicInformation?.isAdultsOnly}
                    childCount={childCount}
                    viewRatesURL={buildPropertyAvailabilityUrl(vm.propertyId)}
                    viewRatesLabel="View Rates"
                  />
                </Styled.PriceSectionWrapper>
              </Styled.RatingsRow>
            </div>

            {/* Rich media: preloaded mediaFromPreview is passed through when available */}
            <Styled.ImagesRow className="ml-n4 ml-lg-0">
              <MChatImageCarousel
                propertyName={vm.propertyName}
                propertyData={vm.edge?.node?.property}
                mediaFromPreview={propertyMediaById?.[vm.propertyId]}
                onImageClick={() => handlePropertyClick(vm, { openGallery: true })}
              />
            </Styled.ImagesRow>

            {/* Formatter-generated description (supports markdown) - use hqvComponents for hqv:// links */}
            {vm.description ? (
              <div className="pr-4 t-font-s">
                <MarkdownPreview source={vm.description} components={hqvComponents} />
              </div>
            ) : null}

            {/* Keep CTA behavior aligned with the title action by opening HQV for the same property payload. */}
            <MButton
              variant={MButtonType.Tertiary}
              size={MButtonSize.L}
              type="button"
              aria-label={`View details for ${vm.propertyName}`}
              onClick={() => handlePropertyClick(vm)}
              className="align-self-start custom_click_track m-integrated-imagery-view-details"
              custom_click_track_value={AI_SEARCH_CLICK_TRACK.VIEW_RATES}
            >
              View Details
            </MButton>
          </Styled.PropertyBlock>
        );
      })}

      <MResponseActions
        messageId={messageId}
        allowViewAllProperties
        viewAllCopy="integratedResults"
        totalResults={totalResults}
        thumbFeedback={thumbFeedback}
        onThumbsUp={
          onThumbFeedback
            ? () => {
                if (thumbFeedback) return;
                onThumbFeedback('up');
              }
            : undefined
        }
        onThumbsDown={
          onThumbFeedback
            ? () => {
                if (thumbFeedback) return;
                onThumbFeedback('down');
              }
            : undefined
        }
        onViewAll={() => onViewAllProperties?.(viewData)}
      />

      {/* Optional trailing guidance from formatter - use hqvComponents for hqv:// links */}
      {followUp ? (
        <div className="pr-4 t-font-s">
          <MarkdownPreview source={followUp} components={hqvComponents} />
        </div>
      ) : null}

      {/* Suggested follow-up prompts rendered as chips */}
      {chipItems.length > 0 ? <MChipGroup chips={chipItems} variant="chat" /> : null}
    </Styled.Container>
  );
};
